import { gql, useApolloClient, useMutation, useQuery } from "@apollo/client";
import { Alert, Box, Checkbox, FormControlLabel, MenuItem, Stack } from "@mui/material";
import { useCallback, useContext, useState, useEffect } from "react";
import {
  PRODUCTS,
  PROPOSAL,
  SAVE_ITEM,
  SAVE_PRODUCT,
  SETTINGS,
  UPDATE_LIBRARY_PRODUCT,
} from "./api";
import UiButton from "components/base/UiButton";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";

import CustomEditor from "./CustomEditor";
import { ProposalFormsContext } from "modules/proposals/providers/ProposalFormsContextProvider";

function ProposalCustomEditor({
  formId,
  ...props
}: React.ComponentProps<typeof CustomEditor> & { formId: string }) {
  const { setProductDirtyStatus, setProductSubmitFunction, setProductFormStatus } =
    useContext(ProposalFormsContext);
  const onDirtyChange = useCallback(
    (dirty: boolean) => setProductDirtyStatus(formId, dirty),
    [formId, setProductDirtyStatus]
  );
  const registerSave = useCallback(
    (save: () => Promise<{ status: boolean; data: null }>) =>
      setProductSubmitFunction(formId, save),
    [formId, setProductSubmitFunction]
  );
  const onSaveStatusChange = useCallback(
    (status: "pending" | "fulfilled" | "rejected") => setProductFormStatus(formId, status),
    [formId, setProductFormStatus]
  );
  return (
    <CustomEditor
      {...props}
      onDirtyChange={onDirtyChange}
      registerSave={registerSave}
      onSaveStatusChange={onSaveStatusChange}
    />
  );
}
import { blankCustom, Custom, customInput } from "./calculator";
const DELETE = gql`
  mutation DeleteCustomItem($id: ID!) {
    deleteProposalProduct(id: $id) {
      id
    }
  }
`;
const OPTIONAL = gql`
  mutation SetCustomOptional($id: ID!, $isOptional: Boolean!) {
    toggleProposalProductOptionality(id: $id, isOptional: $isOptional) {
      id
    }
  }
`;

type Props = {
  proposalId: string;
  stageId: string;
  readOnly: boolean;
  onArrange: () => void;
  travelHours?: string;

  startBlankRequest?: number;
  renderProducts: (
    renderCustom: (id: string, hideOptional?: boolean) => React.ReactNode
  ) => React.ReactNode;
};
export default function CustomItems({
  proposalId,
  stageId,
  readOnly,
  onArrange,
  travelHours,

  startBlankRequest,
  renderProducts,
}: Props) {
  const client = useApolloClient();
  const { data, error } = useQuery(PROPOSAL, { variables: { id: proposalId } });
  const { data: settings } = useQuery(SETTINGS);
  const [updateLibrary] = useMutation(UPDATE_LIBRARY_PRODUCT);
  const [save] = useMutation(SAVE_ITEM);
  const [saveProduct] = useMutation(SAVE_PRODUCT);
  const [remove] = useMutation(DELETE);
  const [optional] = useMutation(OPTIONAL);
  const [adding, setAdding] = useState<Custom | null>(null);
  const newItem = () => ({
    ...blankCustom(settings?.customSettings.markup),
    pricing: { version: 1 as const, mode: "automatic" as const, value: "0", discount: "0" },
    calculationVersion: 2,
    helpers: {
      hoursPerDay: String(settings?.customSettings.workingHoursPerDay || "8"),
      travel: { enabled: false, roundTripHours: travelHours ?? "0" },
    },
  });
  useEffect(() => {
    if (startBlankRequest) setAdding((current) => current ?? newItem());
  }, [startBlankRequest]);
  const [failure, setFailure] = useState("");
  const [busy, setBusy] = useState(false);
  const items =
    data?.customProposal.stages.find((stage: any) => stage.id === stageId)?.proposalProducts ?? [];
  const refresh = async () => {
    await client.refetchQueries({ include: [PROPOSAL, "customGetProposal"] });
  };
  const store = async (input: Custom, id?: string, discount?: string) => {
    await save({ variables: { id, stageId, input: customInput(input), discount } });
    await refresh();
  };
  const storeProduct = settings?.customSettings.canManageProducts
    ? async (input: Custom, productTypeId: string) => {
        await saveProduct({
          variables: { input: customInput(input), productTypeId },
          refetchQueries: [PRODUCTS],
        });
      }
    : undefined;
  const act = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setFailure("");
    try {
      await action();
      await refresh();
    } catch (e) {
      setFailure((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const renderCustom = (id: string, hideOptional = false) => {
    const product = items.find((item: any) => item.id === id);
    if (!product?.custom) return null;
    return (
      <ProposalCustomEditor
        sharedColumnHeaders
        formId={product.id}
        optionalControl={
          !readOnly &&
          !hideOptional && (
            <FormControlLabel
              sx={{
                m: 0,
                "& .MuiCheckbox-root": { p: 0.5 },
                "& .MuiFormControlLabel-label": {
                  fontSize: 12,
                  display: { xs: "inline", lg: "none" },
                },
              }}
              label="Optional"
              control={
                <Checkbox
                  size="small"
                  inputProps={{ "aria-label": "Optional" }}
                  checked={product.isOptional}
                  disabled={busy}
                  onChange={(_, checked) =>
                    act(() => optional({ variables: { id: product.id, isOptional: checked } }))
                  }
                />
              }
            />
          )
        }
        actions={
          !readOnly
            ? (close) => [
                <MenuItem
                  key="duplicate"
                  disabled={busy}
                  onClick={() => {
                    act(() => store(product.custom, undefined, product.discount));
                    close();
                  }}
                >
                  Duplicate line item
                </MenuItem>,
                <MenuItem
                  key="delete"
                  disabled={busy}
                  sx={{ color: draftColors.red }}
                  onClick={() => {
                    act(() => remove({ variables: { id: product.id } }));
                    close();
                  }}
                >
                  Delete line item
                </MenuItem>,
              ]
            : undefined
        }
        initial={product.custom}
        allowDiscount
        discountCents={product.discount}
        readOnly={readOnly}
        onSave={(input, discount) => store(input, product.id, discount)}
        onUpdateProduct={
          storeProduct && !readOnly && product.custom.sourceProductRevision
            ? async (input) => {
                const result = await updateLibrary({
                  variables: {
                    id: input.sourceProductId,
                    expectedRevision: input.sourceProductRevision,
                    input: customInput(input),
                  },
                });
                return result.data.updateCustomLibraryProduct.revision;
              }
            : undefined
        }
        onSaveProduct={readOnly ? undefined : storeProduct}
      />
    );
  };
  return (
    <Box sx={{ minWidth: 0 }}>
      {(error || failure) && <Alert severity="error">{error?.message || failure}</Alert>}
      {renderProducts(renderCustom)}
      {adding && !readOnly && (
        <>
          <ProposalCustomEditor
            sharedColumnHeaders
            formId={`custom-new-${stageId}`}
            initial={adding}
            allowDiscount
            key={JSON.stringify(adding)}
            onSave={async (input, discount) => {
              await store(input, undefined, discount);
              setAdding(null);
            }}
            onSaveProduct={storeProduct}
            actions={(close) => (
              <MenuItem
                sx={{ color: draftColors.red }}
                onClick={() => {
                  close();
                  setAdding(null);
                }}
              >
                Delete line item
              </MenuItem>
            )}
          />
        </>
      )}
      {!readOnly && items.length > 1 && (
        <UiButton uiVariant="outlined-small" onClick={onArrange}>
          Arrange products
        </UiButton>
      )}
    </Box>
  );
}
